import { useForm } from "react-hook-form";

import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { MakeCombobox } from "../parts/MakeCombobox";
import { ModelCombobox } from "../parts/ModelCombobox";
import { YearCombobox } from "../parts/YearCombobox";
import useNotification from "../notifications/useNotifications";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import { ICreatePartRequest, IPartRequest, PartRequestContactPreference } from "../../models/partRequests/IPartRequest";
import {
  PartRequestFormValues,
  toCreatePartRequest,
  validatePartRequestForm,
  yearFromVehicleLookup,
} from "../../utils/partRequests";

interface CreatePartRequestFormProps {
  /** When editing, the existing request pre-fills the form. */
  existing?: IPartRequest;
  onSubmit: (request: ICreatePartRequest) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const contactOptions = [
  { id: PartRequestContactPreference.Phone, name: "Phone" },
  { id: PartRequestContactPreference.Email, name: "Email" },
];

function toFormValues(existing?: IPartRequest): PartRequestFormValues {
  return {
    customerName: existing?.customerName ?? "",
    customerPhone: existing?.customerPhone ?? "",
    customerEmail: existing?.customerEmail ?? "",
    contactPreference: existing?.contactPreference ?? PartRequestContactPreference.Phone,
    vrm: existing?.vrm ?? "",
    make: existing?.make ?? "",
    model: existing?.model ?? "",
    year: existing?.year ? String(existing.year) : "",
    partWanted: existing?.partWanted ?? "",
    details: existing?.details ?? "",
  };
}

export default function CreatePartRequestForm({ existing, onSubmit, onCancel, isLoading }: CreatePartRequestFormProps) {
  const { addNotification } = useNotification();
  const { vrmLookupData } = useVehiclesMutations();
  const { register, handleSubmit, setValue, watch } = useForm<PartRequestFormValues>({
    defaultValues: toFormValues(existing),
  });

  const watchVrm = watch("vrm");
  const watchMake = watch("make");
  const watchModel = watch("model");
  const watchYear = watch("year");

  const lookupVehicle = () => {
    const vrm = (watchVrm ?? "").trim();
    if (!vrm || vrmLookupData.isLoading) return;
    vrmLookupData.mutate(vrm, {
      onSuccess: (data) => {
        const vehicle = data?.data;
        if (!vehicle) {
          addNotification({ variant: "warn", primaryText: "Vehicle not found for that registration" });
          return;
        }
        const make = (vehicle.combinedMake ?? "").trim();
        const model = (vehicle.modelRangeDescription || vehicle.combinedModel || "").trim();
        const year = yearFromVehicleLookup(vehicle);
        if (vehicle.vrm) setValue("vrm", vehicle.vrm);
        setValue("make", make);
        setValue("model", model);
        setValue("year", year);
        if (!make && !model) {
          addNotification({ variant: "warn", primaryText: "Lookup came back without a make or model" });
          return;
        }
        addNotification({
          variant: "success",
          primaryText: ["Found vehicle", make, model, year].filter(Boolean).join(" — "),
        });
      },
      onError: () => {
        addNotification({ variant: "error", primaryText: "Vehicle lookup failed" });
      },
    });
  };

  const submit = (values: PartRequestFormValues) => {
    const problem = validatePartRequestForm(values);
    if (problem) {
      addNotification({ variant: "warn", primaryText: problem });
      return;
    }
    onSubmit(toCreatePartRequest(values));
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-6 p-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">The part</h3>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInput label="Part wanted" register={register} registerName="partWanted" required cols="sm:col-span-2" placeholder="e.g. Driver side front door, alternator, N/S rear light" />
          <TextInput label="Details" register={register} registerName="details" richText rows={3} cols="sm:col-span-2" placeholder="Colour, trim, engine code, anything that narrows it down" />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">The vehicle</h3>
        <div className="mt-2 grid grid-cols-1 items-end gap-4 sm:grid-cols-[1fr_auto]">
          <TextInput label="Registration" register={register} registerName="vrm" placeholder="AB12 CDE" customClasses="uppercase" />
          <ButtonInput
            label="Look up"
            onClick={lookupVehicle}
            isLoading={vrmLookupData.isLoading}
            disabled={!watchVrm?.trim()}
            classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <MakeCombobox
            label="Make"
            currentValue={watchMake}
            onMakeSelect={(selected) => {
              setValue("make", selected?.make ?? "");
              setValue("model", "");
            }}
          />
          <ModelCombobox
            label="Model"
            make={watchMake}
            currentValue={watchModel}
            onModelSelect={(selected) => setValue("model", selected?.range ?? "")}
          />
          <YearCombobox
            label="Year"
            currentValue={watchYear}
            placeholder="Select year..."
            onYearSelect={(selected) => setValue("year", selected ?? "")}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">Look up a registration to fill make, model and year, or pick them from the lists. A registration or at least the make is needed so we can match vehicles in the yard.</p>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">The customer</h3>
        <p className="text-xs text-gray-500">Kept in Wilma only - never shared if the request goes out to the network.</p>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInput label="Name" register={register} registerName="customerName" required />
          <SelectInput label="Prefers" register={register} registerName="contactPreference" options={contactOptions} setValue={setValue} watch={watch} />
          <TextInput label="Phone" register={register} registerName="customerPhone" type="tel" />
          <TextInput label="Email" register={register} registerName="customerEmail" type="email" />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <ButtonInput label="Cancel" classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300" onClick={onCancel} />
        <ButtonInput label={existing ? "Save changes" : "Log request"} isSubmit onClick={undefined} classes="" isLoading={isLoading} />
      </div>
    </form>
  );
}
